import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { getCurrentUser, login as authLogin, register as authRegister, logout as authLogout, User, setupAxiosInterceptors } from '../utils/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setupAxiosInterceptors();
    
    const initAuth = async () => {
      try {
        console.log('Initializing authentication');
        // Using mock data if API fails
        const currentUser = await getCurrentUser(true);
        console.log('Auth initialized with user:', currentUser);
        setUser(currentUser);
      } catch (err) {
        console.error('Failed to fetch user during initialization', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      // Using mock data if API fails
      const response = await authLogin(email, password, true);
      console.log('Login successful for user:', response.user);
      setUser(response.user);
    } catch (err: any) {
      console.error('Login error in AuthContext:', err);
      setError(err.response?.data?.message || 'Failed to login');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      setLoading(true);
      setError(null);
      // Using mock data if API fails
      const response = await authRegister(email, password, name, '', true);
      console.log('Registration successful for user:', response.user);
      setUser(response.user);
    } catch (err: any) {
      console.error('Registration error in AuthContext:', err);
      setError(err.response?.data?.message || 'Failed to register');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    console.log('Logging out user');
    authLogout();
    setUser(null);
  };

  const contextValue = {
    user,
    loading,
    error,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 